import { defineCatalog } from '@json-render/core'
import { defineRegistry, useStateStore } from '@json-render/vue'
import { schema } from '@json-render/vue/schema'
import { h, inject } from 'vue'
import { z } from 'zod'

import {
  CLIENTPORTAL_V2_SCREEN_RUNTIME_KEY,
  type ClientPortalV2Handler,
} from '@clientportal/customization-layer/experience/clientportal-experience-context'
import { CLIENTPORTAL_V2_COMPONENT_IMPLEMENTATIONS } from './clientportal-v2-component-implementations'
import type { StateStore } from '@json-render/core'

interface ClientPortalV2EventDescriptor {
  readonly intent: string
  readonly handler: string
  readonly params?: (store: Pick<StateStore, 'get'>) => Record<string, unknown>
}

interface ClientPortalV2RuntimeDescriptor {
  readonly model?: Readonly<Record<string, string>>
  readonly events?: Readonly<Record<string, ClientPortalV2EventDescriptor>>
}

const value = (path: string) => (store: Pick<StateStore, 'get'>) => store.get(path)

const event = (
  intent: string,
  handler: string,
  params?: (store: Pick<StateStore, 'get'>) => Record<string, unknown>
): ClientPortalV2EventDescriptor => ({ intent, handler, params })

const models = {
  'clientportal.events.live-now': {
    state: '/resources/events/liveNow/state',
    events: '/resources/events/liveNow/items',
    total: '/resources/events/liveNow/total',
  },
  'clientportal.events.list': {
    upcomingState: '/resources/events/upcoming/state',
    upcomingEvents: '/resources/events/upcoming/items',
    pastState: '/resources/events/past/state',
    pastEvents: '/resources/events/past/items',
    upcomingTotal: '/resources/events/upcoming/total',
    pastTotal: '/resources/events/past/total',
  },
  'clientportal.events.detail': {
    state: '/resources/events/detail/state',
    event: '/resources/events/detail/item',
    isRegistered: '/resources/events/detail/isRegistered',
    speakerDetail: '/resources/events/speakerDetail',
    exhibitorDetail: '/resources/events/exhibitorDetail',
  },
  'clientportal.events.directory': {
    state: '/resources/events/directory/state',
    events: '/resources/events/directory/items',
    hasNextPage: '/resources/events/directory/hasNextPage',
    isFetchingNextPage: '/resources/events/directory/isFetchingNextPage',
    isSearchActive: '/resources/events/directory/isSearchActive',
  },
} as const

const events = {
  'clientportal.events.live-now': {
    openEvent: event('events.open', 'events.openEvent', store => ({
      eventId: value('/ui/selectedEventId')(store),
    })),
    seeAll: event('events.seeAll', 'events.seeAll', () => ({
      tab: 'live',
    })),
  },
  'clientportal.events.list': {
    openEvent: event('events.open', 'events.openEvent', store => ({
      eventId: value('/ui/selectedEventId')(store),
    })),
    seeAll: event('events.list.open', 'events.seeAll', store => ({
      tab: value('/ui/eventsListTab')(store),
    })),
  },
  'clientportal.events.detail': {
    saveSession: event('events.agenda.save', 'events.detail.saveAgendaSession', store => ({
      sessionId: value('/ui/selectedAgendaSessionId')(store),
    })),
    addToCalendar: event('events.agenda.addToCalendar', 'events.detail.addToCalendar', store => ({
      sessionId: value('/ui/selectedCalendarSessionId')(store),
    })),
    register: event('events.register', 'events.detail.register'),
    downloadTicket: event('events.ticket.download', 'events.detail.downloadTicket', store => ({
      ticketId: value('/ui/selectedTicketId')(store),
    })),
    chat: event('events.attendee.chat', 'events.detail.chat', store => ({
      attendeeId: value('/ui/selectedAttendeeId')(store),
    })),
    openSpeaker: event('events.speaker.open', 'events.detail.openSpeaker', store => ({
      speakerId: value('/ui/selectedSpeakerId')(store),
    })),
    closeSpeaker: event('events.speaker.close', 'events.detail.closeSpeaker'),
    openExhibitor: event('events.exhibitor.open', 'events.detail.openExhibitor', store => ({
      exhibitorId: value('/ui/selectedExhibitorId')(store),
    })),
  },
  'clientportal.events.directory': {
    openEvent: event('events.open', 'events.openEvent', store => ({
      eventId: value('/ui/selectedEventId')(store),
    })),
    loadMore: event('events.directory.loadMore', 'events.directory.loadMore'),
  },
} as const satisfies Readonly<Record<string, Readonly<Record<string, ClientPortalV2EventDescriptor>>>>

const runtimeDescriptors = Object.fromEntries(
  Object.keys(CLIENTPORTAL_V2_COMPONENT_IMPLEMENTATIONS).map(capability => [
    capability,
    {
      ...(models[capability as keyof typeof models] ? { model: models[capability as keyof typeof models] } : {}),
      ...(events[capability as keyof typeof events] ? { events: events[capability as keyof typeof events] } : {}),
    },
  ])
) as Record<string, ClientPortalV2RuntimeDescriptor>

export const CLIENTPORTAL_V2_RUNTIME_DESCRIPTORS = Object.freeze(runtimeDescriptors)

export const resolveClientPortalCapabilityPropsV2 = (
  capability: string,
  config: Readonly<Record<string, unknown>>,
  store: Pick<StateStore, 'get'>
): Record<string, unknown> => {
  const model = CLIENTPORTAL_V2_RUNTIME_DESCRIPTORS[capability]?.model ?? {}
  return {
    ...Object.fromEntries(Object.entries(model).map(([prop, path]) => [prop, store.get(path)])),
    ...structuredClone(config),
  }
}

export const dispatchClientPortalCapabilityEventV2 = async ({
  capability,
  event: eventName,
  store,
  handlers,
}: {
  readonly capability: string
  readonly event: string
  readonly store: Pick<StateStore, 'get'>
  readonly handlers: Readonly<Record<string, ClientPortalV2Handler>>
}): Promise<void> => {
  const descriptor = CLIENTPORTAL_V2_RUNTIME_DESCRIPTORS[capability]?.events?.[eventName]
  const handler = descriptor ? handlers[descriptor.handler] : undefined
  if (!descriptor || !handler) return
  await handler(descriptor.params?.(store) ?? {})
}

const anyProps = z.record(z.string(), z.unknown())
const componentDefinitions = Object.fromEntries(
  Object.keys(CLIENTPORTAL_V2_RUNTIME_DESCRIPTORS).map(capability => [
    capability,
    { props: anyProps, slots: ['default'], description: `Trusted web runtime adapter for ${capability}.` },
  ])
)
componentDefinitions['clientportal.system.unsupported-capability'] = {
  props: anyProps,
  slots: ['default'],
  description: 'Built-in fallback for an unsupported capability version.',
}

export const CLIENTPORTAL_V2_RUNTIME_CATALOG = defineCatalog(schema, { components: componentDefinitions })

const componentImplementations = Object.fromEntries(
  Object.entries(CLIENTPORTAL_V2_RUNTIME_DESCRIPTORS).map(([capability]) => [
    capability,
    ({ props, children }: { props: Record<string, unknown>; children?: unknown }) => {
      const store = useStateStore()
      const screenRuntime = inject(CLIENTPORTAL_V2_SCREEN_RUNTIME_KEY, null)
      const implementation = CLIENTPORTAL_V2_COMPONENT_IMPLEMENTATIONS[capability]
      return implementation({
        props: resolveClientPortalCapabilityPropsV2(capability, props, store),
        children,
        emit: eventName => {
          void dispatchClientPortalCapabilityEventV2({
            capability,
            event: eventName,
            store,
            handlers: screenRuntime?.handlers ?? {},
          })
        },
      })
    },
  ])
)

componentImplementations['clientportal.system.unsupported-capability'] = ({
  props,
}: {
  props: { capability?: string; requestedVersion?: number }
}) =>
  h(
    'div',
    {
      'data-sdui-fallback': 'unsupported-capability',
      'data-capability': props.capability,
      'data-requested-version': props.requestedVersion,
      class: 'hidden',
    },
    []
  )

export const { registry: CLIENTPORTAL_V2_RUNTIME_COMPONENTS } = defineRegistry(CLIENTPORTAL_V2_RUNTIME_CATALOG, {
  components: componentImplementations,
})

export const CLIENTPORTAL_V2_RUNTIME = Object.freeze({
  catalog: CLIENTPORTAL_V2_RUNTIME_CATALOG,
  registry: CLIENTPORTAL_V2_RUNTIME_COMPONENTS,
})
